import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../../components/Navigation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Partners() {
  const [heroSection, setHeroSection] = useState<any>(null);
  const [partnerBenefits, setPartnerBenefits] = useState<any[]>([]);
  const [partnerTiers, setPartnerTiers] = useState<any[]>([]);
  const [successStories, setSuccessStories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    companySize: '',
    industry: '',
    partnerType: '',
    experience: '',
    message: ''
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      // Fetch hero section
      const { data: heroData } = await supabase
        .from('hero_sections')
        .select('*')
        .eq('page', 'partners')
        .eq('is_active', true)
        .single();

      if (heroData) setHeroSection(heroData);

      // Fetch partner benefits
      const { data: benefitsData } = await supabase
        .from('partner_benefits')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (benefitsData) setPartnerBenefits(benefitsData);

      // Fetch partner tiers
      const { data: tiersData } = await supabase
        .from('partner_tiers')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (tiersData) setPartnerTiers(tiersData);

      // Fetch success stories
      const { data: storiesData } = await supabase
        .from('success_stories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (storiesData) setSuccessStories(storiesData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.companyName || !formData.contactName || !formData.email || !formData.companySize || !formData.industry || !formData.partnerType) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4p4r7hmma3c958r7rbg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSubmit as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          companySize: '',
          industry: '',
          partnerType: '',
          experience: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Partner Program - Join Our Network',
    description:
      'Become a trusted partner and help enterprises transform their software development processes. Unlock new revenue opportunities with our partner program.',
    url: `${import.meta.env.VITE_SITE_URL}/partners`,
    mainEntity: {
      '@type': 'Organization',
      name: 'Solution Platform Partner Program',
      description: 'Partner network for resellers and implementation specialists',
      offers: [
        {
          '@type': 'Offer',
          name: 'Reseller Partner Program',
          description: '20-30% recurring commission for platform sales'
        },
        {
          '@type': 'Offer',
          name: 'Implementation Partner Program',
          description: '15-25% project-based commission for consulting services'
        }
      ]
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Partner Program - Join Our Network | Solution Platform"
        description="Become a trusted partner and help enterprises transform their software development processes. Unlock new revenue opportunities with our reseller and implementation partner programs."
        keywords="partner program, reseller program, implementation partner, software partner, enterprise solutions partner"
        canonical="/partners"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <Navigation />

      {/* Back Button */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center bg-no-repeat py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('${heroSection?.background_image || 'https://public.readdy.ai/ai/img_res/cc239be774bd1a80b6ebc3d735b95f28.jpg'}')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            {heroSection?.title || 'Join Our Partner Network'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
            {heroSection?.subtitle || 'Become a trusted partner and help enterprises transform their software development processes. Unlock new revenue opportunities and provide cutting‑edge solutions to your clients.'}
          </p>
          <button className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
            {heroSection?.cta_text || 'Apply Now'}
          </button>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Partner with Us?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our partner program is designed to help you grow your business while providing exceptional value to your clients.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {partnerBenefits.length > 0 ? partnerBenefits.map((benefit) => (
              <div key={benefit.id} className="text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6`} style={{ backgroundColor: `${benefit.icon_color || '#3B82F6'}20` }}>
                  <i className={`${benefit.icon || 'ri-star-line'} text-3xl`} style={{ color: benefit.icon_color || '#3B82F6' }}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            )) : (
              <>
                {/* Default benefits fallback */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-money-dollar-circle-line text-3xl text-blue-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Lucrative Revenue Sharing</h3>
                  <p className="text-gray-600">
                    Earn up to 30% recurring commission on all sales you generate. Our transparent revenue sharing model ensures you're rewarded for your success.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-graduation-cap-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive Training</h3>
                  <p className="text-gray-600">
                    Access extensive training materials, certification programs, and ongoing education to become an expert in our platform suite.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-customer-service-2-line text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Dedicated Support</h3>
                  <p className="text-gray-600">
                    Get dedicated partner support, technical assistance, and sales enablement resources to help you succeed with every client engagement.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Partner Program Types</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the partnership model that best fits your business goals and capabilities.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {partnerTiers.length > 0 ? partnerTiers.map((tier) => (
              <div key={tier.id} className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center mr-4`} style={{ backgroundColor: `${tier.icon_color || '#3B82F6'}20` }}>
                    <i className={`${tier.icon || 'ri-store-line'} text-2xl`} style={{ color: tier.icon_color || '#3B82F6' }}></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{tier.name}</h3>
                    <p className="text-gray-600">{tier.subtitle}</p>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.benefits && Array.isArray(tier.benefits) && tier.benefits.map((benefit: string, idx: number) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <button className={`w-full text-white py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer`} style={{ backgroundColor: tier.icon_color || '#3B82F6' }}>
                  {tier.cta_text || 'Apply Now'}
                </button>
              </div>
            )) : (
              <>
                {/* Default tiers fallback */}
                {/* ... existing default tier code ... */}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Partner Application Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Apply to Become a Partner</h2>
            <p className="text-xl text-gray-600">
              Fill out the form below and our partner team will review your application within 2 business days.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                  <div>
                    <h3 className="text-green-800 font-semibold">Application Submitted Successfully!</h3>
                    <p className="text-green-700">Thank you for your interest in partnering with us. Our team will review your application and contact you within 2 business days.</p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                  <div>
                    <h3 className="text-red-800 font-semibold">Please check your information</h3>
                    <p className="text-red-700">Make sure all required fields are filled out correctly and try again.</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="partner-application-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
                    focus:border-transparent"
                    placeholder="your.email@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
                    focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Size *
                  </label>
                  <select
                    id="companySize"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
                    focus:border-transparent pr-8"
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-1000">201-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
                    focus:border-transparent pr-8"
                  >
                    <option value="">Select industry</option>
                    <option value="technology">Technology</option>
                    <option value="consulting">Consulting</option>
                    <option value="finance">Finance</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="partnerType" className="block text-sm font-medium text-gray-700 mb-2">
                    Partner Type Interest *
                  </label>
                  <select
                    id="partnerType"
                    name="partnerType"
                    value={formData.partnerType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
                    focus:border-transparent pr-8"
                  >
                    <option value="">Select partner type</option>
                    <option value="reseller">Reseller Partner</option>
                    <option value="implementation">Implementation Partner</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                    Experience with Similar Platforms
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2
                    focus:ring-blue-5 focus:border-transparent pr-8"
                  >
                    <option value="">Select experience level</option>
                    <option value="none">No experience</option>
                    <option value="basic">Basic (1-2 years)</option>
                    <option value="intermediate">Intermediate (3-5 years)</option>
                    <option value="advanced">Advanced (5+ years)</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your business and why you want to partner with us
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
                  focus:border-transparent resize-none"
                  placeholder="Describe your business, target customers, and partnership goals..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </p>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Partner Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our partners are growing their businesses and delivering exceptional value to their clients.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {successStories.length > 0 ? successStories.map((story) => (
              <div key={story.id} className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center mb-6">
                  <img
                    src={story.avatar || `https://readdy.ai/api/search-image?query=Professional%20business%20executive%20headshot&width=80&height=80&seq=${story.id}&orientation=squarish`}
                    alt={story.company_name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{story.company_name}</h3>
                    <p className="text-gray-600">{story.partner_type}</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">{story.testimonial}</p>
                <div className="text-sm text-gray-600">
                  <strong>{story.contact_name}</strong>, {story.contact_title}
                </div>
              </div>
            )) : (
              <>
                {/* Default success stories fallback */}
                {/* ... existing default stories code ... */}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-12 w-auto mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Platforms</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/platforms" className="hover:text-white cursor-pointer">
                    Codee Platform
                  </Link>
                </li>
                <li>
                  <Link to="/platforms" className="hover:text-white cursor-pointer">
                    Fusion Platform
                  </Link>
                </li>
                <li>
                  <Link to="/platforms" className="hover:text-white cursor-pointer">
                    BI Platform
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/resources" className="hover:text-white cursor-pointer">
                    User Guides
                  </Link>
                </li>
                <li>
                  <Link to="/features" className="hover:text-white cursor-pointer">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/partners" className="hover:text-white cursor-pointer">
                    Partner Program
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>support@company.com</li>
                <li>+1 (555) 123-4567</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>
              &copy; 2024 Solution Platform. All rights reserved. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="hover:text-white cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
